import { memo, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { analytics } from '@/features/analytics/service';
import { getEnvVar } from '@/shared/utils/envVarConfig';
import { isNativePlatform } from '@/shared/utils/platform';

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RELOAD_LOOP_KEY = 'version-check-reloads';
const COOLDOWN_KEY = 'version-check-cooldown';
const MAX_RELOADS = 3;
const RELOAD_WINDOW_MS = 60_000; // 1 minute
const COOLDOWN_MS = 5 * 60 * 1000; // 5 minute cooldown after hitting limit

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch('/version', { method: 'HEAD' });
    if (!response.ok) return null;
    return response.headers.get('X-Duos-Version')?.trim() ?? null;
  } catch {
    return null; // Offline or network error
  }
}

/**
 * Check if we're in cooldown period after too many reload attempts.
 */
function isInCooldown(): boolean {
  const cooldownUntil = sessionStorage.getItem(COOLDOWN_KEY);
  if (!cooldownUntil) return false;
  return Date.now() < Number(cooldownUntil);
}

/**
 * Enter cooldown period - no auto-reloads for COOLDOWN_MS.
 */
function enterCooldown(): void {
  sessionStorage.setItem(COOLDOWN_KEY, String(Date.now() + COOLDOWN_MS));
}

/**
 * Check if we can safely reload (not too many recent attempts).
 * Returns true if safe, false if limit reached (enters cooldown).
 */
function canSafelyReload(): boolean {
  const now = Date.now();
  const stored = sessionStorage.getItem(RELOAD_LOOP_KEY);
  const reloads: number[] = stored ? (JSON.parse(stored) as number[]) : [];

  // Filter to only reloads within the window
  const recentReloads = reloads.filter(t => now - t < RELOAD_WINDOW_MS);

  if (recentReloads.length >= MAX_RELOADS) {
    enterCooldown();
    return false;
  }

  // Record this reload attempt
  recentReloads.push(now);
  sessionStorage.setItem(RELOAD_LOOP_KEY, JSON.stringify(recentReloads));
  return true;
}

/**
 * Show a toast prompting user to manually refresh.
 */
function showUpdateToast(): void {
  toast('New version available', {
    description: 'Tap to refresh and get the latest updates.',
    duration: Infinity, // Persist until dismissed or clicked
    action: {
      label: 'Refresh',
      onClick: () => {
        window.location.reload();
      },
    },
  });
}

function useVersionCheck() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastShownRef = useRef(false);

  useEffect(() => {
    // Skip on native platforms - app updates via app stores
    if (isNativePlatform()) return;

    const runtimeVersion = getEnvVar('VITE_APP_VERSION')?.trim();
    if (!runtimeVersion) return;

    let isPaused = false;

    const checkVersion = async () => {
      if (isPaused) return;

      const deployedVersion = await fetchDeployedVersion();
      if (!deployedVersion) return;

      if (deployedVersion !== runtimeVersion) {
        // If in cooldown, show toast (once) but don't auto-reload
        if (isInCooldown()) {
          if (!toastShownRef.current) {
            showUpdateToast();
            toastShownRef.current = true;
          }
          return;
        }

        // Check if we can safely reload
        if (!canSafelyReload()) {
          // Hit the limit - show toast for manual refresh
          showUpdateToast();
          toastShownRef.current = true;
          return;
        }

        // Track analytics (best effort, don't block reload)
        try {
          analytics.captureEvent('App Version Updated', {
            previous_version: runtimeVersion,
            new_version: deployedVersion,
          });
        } catch {
          // Ignore analytics errors
        }

        window.location.reload();
      }
    };

    // Pause checks when tab is hidden to save battery
    const handleVisibilityChange = () => {
      isPaused = document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check after 10s, then every 5 minutes
    const initialTimeout = setTimeout(() => void checkVersion(), 10_000);
    intervalRef.current = setInterval(
      () => void checkVersion(),
      VERSION_CHECK_INTERVAL_MS,
    );

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}

export const VersionCheckHandler = memo(function VersionCheckHandler() {
  useVersionCheck();
  return null;
});
